<template>
  <v-layout row wrap justify-space-between class="typification-form-container">
    <input-field
      value=""
      :label="$tc('Address', 1)"
      size="large"
      inputType="group"
    />

    <input-field
      v-model="addressOrigin"
      label=""
      size="large"
      inputType="radio"
      :radioOptions="addressOriginOptions"
    />

    <div
      v-if="addressOrigin === 'Main' && !hasRequiredAddressFields"
      class="input-field error--text"
      style="font-size: 9px"
    >
      {{ $t("MissingRequiredAddressFields") }}
    </div>

    <v-layout
      v-if="addressOrigin === 'Alternative'"
      row
      wrap
      justify-space-between
      style="width: 100%"
    >
      <input-field
        v-model="addressType"
        :label="$t('AddressType')"
        size="25"
        inputType="select"
        selectOptions="list_address_type"
        :required="true"
      />

      <input-field
        v-model="address"
        :label="$tc('Address', 1)"
        size="45"
        inputType="text"
        :required="true"
      />

      <input-field
        v-model="number"
        :label="$tc('Number', 1)"
        size="15"
        inputType="text"
        :required="true"
      />

      <input-field :value="noNumber" label=" " size="15" inputType="checkbox">
        <div class="checkbox-group">
          <v-checkbox
            v-model="noNumber"
            label="S/N"
            color="accent"
            :ripple="false"
          />
        </div>
      </input-field>

      <input-field v-model="cp4" label="CP4" size="15" inputType="number" />

      <input-field v-model="cp3" label="CP3" size="15" inputType="number" />

      <input-field
        v-model="locale"
        :label="$t('Locale')"
        size="70"
        inputType="text"
      />
    </v-layout>

    <input-field
      value=""
      :label="$t('Typification')"
      size="large"
      inputType="group"
    />

    <v-layout row wrap justify-space-between align-end style="width: 100%">
      <input-field
        v-model="typificationType"
        :label="$tc('Type', 1)"
        size="25"
        inputType="select"
        selectOptions="list_bd_category_type"
        :required="true"
      />

      <input-field
        v-model="fromFloor"
        :label="$t('FromFloor')"
        size="25"
        inputType="number"
        :required="true"
      />

      <input-field
        v-model="toFloor"
        :label="$t('ToFloor')"
        size="25"
        inputType="number"
        :number-field-min="parseInt(this.fromFloor)"
        :required="true"
      />

      <input-field
        v-model="fraction"
        :label="$tc('Fraction', 1)"
        size="25"
        inputType="text"
        :required="true"
      />

      <input-field
        v-model="description"
        :label="$t('Description')"
        size="large"
        inputType="text"
      />

      <v-layout justify-end style="margin-right: 20px; margin-top: 10px">
        <v-btn
          @click="addTypification"
          x-small
          icon
          color="accent"
          :disabled="addBtnDisabled"
          :loading="loading"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import { listsMixin } from "@/mixins/listsMixin";

export default {
  name: "TypificationForm",

  components: { InputField },

  mixins: [listsMixin],

  props: {
    objectData: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      addressOrigin: "Main",

      addressType: undefined,
      address: undefined,
      number: undefined,
      noNumber: false,
      cp4: undefined,
      cp3: undefined,
      locale: undefined,

      typificationType: "1",
      fromFloor: undefined,
      toFloor: undefined,
      fraction: undefined,
      description: undefined,

      addressTypeOptions: [],

      loading: false
    };
  },

  computed: {
    addressOriginOptions() {
      return [
        { label: this.$i18n.t("MainAddress"), value: "Main" },
        { label: this.$i18n.t("AlternativeAddress"), value: "Alternative" }
      ];
    },

    hasRequiredAddressFields() {
      return this.addressType && this.address && this.number;
    },

    addBtnDisabled() {
      return !(
        this.hasRequiredAddressFields &&
        this.fraction &&
        this.fromFloor &&
        this.toFloor
      );
    }
  },

  created() {
    this.loadTypificationTypeOptions();
  },

  mounted() {
    this.$nextTick(() => this.fillDefaultAddressFields());
  },

  methods: {
    ...mapActions(["GET_LIST"]),

    fillDefaultAddressFields() {
      let fieldsToWatch = [
        {
          localName: "addressType",
          dbName: "bd_bld_address_type"
        },
        {
          localName: "address",
          dbName: "bd_demand_point_address"
        },
        {
          localName: "number",
          dbName: "bd_demand_point_number"
        },
        {
          localName: "cp4",
          dbName: "bd_demand_point_cp4"
        },
        {
          localName: "cp3",
          dbName: "bd_demand_point_cp3"
        },
        {
          localName: "locale",
          dbName: "bd_demand_point_locale"
        }
      ];
      for (const field of fieldsToWatch) {
        this[field.localName] = this.objectData[field.dbName];

        this.$watch("objectData." + field.dbName, () => {
          this[field.localName] = this.objectData[field.dbName];
        });
      }
    },

    loadTypificationTypeOptions() {
      let listName = "list_address_type";
      this.addressTypeOptions = this.$store.getters.getListOfOptionsByListName(
        listName,
        [this.addressType]
      );
      if (this.addressTypeOptions === undefined) {
        this.GET_LIST({ listName: listName, code: [this.addressType] })
          .then(list => {
            this.addressTypeOptions = list;
          })
          .catch(() => {
            this.addressTypeOptions = [];
          });
      }
    },

    addTypification() {
      this.loading = true;
      this.$emit("addTypification", this.generateTypification());
      this.loading = false;
    },

    generateTypification() {
      let typificationList = [];
      for (let i = this.fromFloor; i <= this.toFloor; i++) {
        typificationList.push({
          bt_num_pol: this.number,
          bt_floor: i,
          bt_fraction: this.fraction,
          bt_type: this.typificationType,
          bt_description: this.description,
          bt_address: this.getAddressTypeName() + " " + this.address
        });
      }
      return typificationList;
    },

    getAddressTypeName() {
      let addressTypeName = this.addressType;
      let addressType = this.addressTypeOptions.find(
        item => item.code === this.addressType
      );
      if (addressType) {
        addressTypeName = this.getListElementLabel(addressType);
      }
      return addressTypeName;
    }
  },

  watch: {
    addressOrigin(newOrigin) {
      if (newOrigin === "Main") {
        this.fillDefaultAddressFields();
      }
    },

    noNumber(newValue) {
      if (newValue === true) {
        this.number = "SN";
      } else if (this.number === "SN") {
        this.number = undefined;
      }
    },

    number(newValue) {
      this.noNumber = newValue === "SN";
    }
  }
};
</script>

<style scoped>
.typification-form-container {
  width: 100%;
  border-radius: 2px;
  box-shadow: -0.5px 0.5px 2px rgba(0, 0, 0, 0.5);
  padding-top: 10px;
  padding-bottom: 20px;
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Buildings/Typification/TypificationForm.vue